import { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LocationService } from '../services/LocationService';
import { useMobile } from './useMobile';
import { useBottomNav } from '../contexts/BottomNavContext';
import {
  MAP_FILTER_AUTO_DISABLE_RADIUS_KM,
  MAP_FILTER_AUTO_REENABLE_RADIUS_KM,
  MAP_FILTER_AUTO_DISABLE_RATIO,
} from '../constants/mapFilters';

// Sheet height constants (as percentage of viewport)
// DEFAULT_SHEET_FULL is a fallback used before dynamic measurements are available.
// It serves as an initial guess to minimize layout shift during hydration.
const DEFAULT_SHEET_FULL = 80;
const MOBILE_SHEET_PEEK = 45;
const MOBILE_SHEET_MAP = 10;

// Layout constants
// Initial guess for search bar height before DOM measurement.
// Matches (h-[56px] + mt-3 + pb-3) = 80px base height.
const SEARCH_BAR_HEIGHT = 80;

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Reusable hook for managing list/map view state with mobile sheet gestures
 * 
 * @param {Object} config - Configuration object
 * @param {boolean} config.enableMap - Whether map functionality is enabled
 * @param {Function} config.onViewChange - Callback when view mode changes
 * @param {Function} config.onSelectedItemChange - Callback when selected item changes (for map previews)
 * @param {boolean} config.treatTabletAsMobile - Whether to treat tablets as mobile (default: true)
 * @returns {Object} View state and handlers
 */
export const useListMapView = ({
  enableMap = true,
  onViewChange,
  onSelectedItemChange,
  treatTabletAsMobile = true,
  autoEnableMapFilter = false,
} = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile: isMobileDevice, isTablet } = useMobile();
  const { setBottomNavVisible } = useBottomNav();
  
  // Treat tablets as mobile for this page - side-by-side map doesn't work well on narrow screens
  const isMobile = treatTabletAsMobile ? (isMobileDevice || isTablet) : isMobileDevice;
  
  // Read view mode from URL (for back button support)
  const urlViewMode = searchParams.get('view');
  
  // View state
  const [viewMode, setViewMode] = useState('list');
  const [mobileSheetState, setMobileSheetState] = useState('list');
  const [mobileSheetHeight, setMobileSheetHeight] = useState(DEFAULT_SHEET_FULL);
  const [userLocation, setUserLocation] = useState(null);
  const [mapViewportBbox, setMapViewportBbox] = useState(null);
  const [mapViewportCenter, setMapViewportCenter] = useState(null);
  const [mapViewportRadius, setMapViewportRadius] = useState(100);
  const [mapZoomLevel, setMapZoomLevel] = useState(null);
  const [mapUserInteracted, setMapUserInteracted] = useState(false);
  const [mapSyncEnabled, setMapSyncEnabled] = useState(false);
  const [mapFilterManuallyDisabled, setMapFilterManuallyDisabled] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  
  // Animation state - start disabled to prevent the initial "correction" jump
  // We only enable animations after the sheet has snapped to its measured position
  const [sheetAnimationsEnabled, setSheetAnimationsEnabled] = useState(false);
  
  const [selectedItem, setSelectedItem] = useState(null);
  
  const mobileSheetDragState = useRef({
    active: false,
    startY: 0,
    startHeight: DEFAULT_SHEET_FULL,
  });
  const mobileSheetHeightRef = useRef(mobileSheetHeight);
  const mobileSearchHeaderRef = useRef(null);
  const [mobileSearchHeaderHeight, setMobileSearchHeaderHeight] = useState(SEARCH_BAR_HEIGHT);
  const [mobileViewportHeight, setMobileViewportHeight] = useState(null);
  
  // Calculate dynamic full sheet height percentage based on viewport and header
  const mobileSheetFull = useMemo(() => {
    if (!isMobile || !mobileViewportHeight || !mobileSearchHeaderHeight) return DEFAULT_SHEET_FULL;
    // Calculate percentage: (viewport - header) / viewport * 100
    // This ensures the sheet starts exactly where the header ends
    const percent = ((mobileViewportHeight - mobileSearchHeaderHeight) / mobileViewportHeight) * 100;
    return Math.max(50, Math.min(99, percent)); // Clamp to reasonable values
  }, [isMobile, mobileViewportHeight, mobileSearchHeaderHeight]);
  
  // Sync sheet height with dynamic full height when in list mode
  useEffect(() => {
    // Only expand if we are explicitly in 'list' mode (full expansion)
    // We don't auto-expand if we are in 'peek' mode
    if (mobileSheetState === 'list' && !mobileSheetDragState.current.active) {
      setMobileSheetHeight(mobileSheetFull);
    }
  }, [mobileSheetFull, mobileSheetState]);

  // Enable animations after measurements are stable
  useEffect(() => {
    if (!isMobile) {
      setSheetAnimationsEnabled(true);
      return;
    }
    
    // Only enable animations after we have valid measurements
    if (mobileViewportHeight && mobileSearchHeaderHeight) {
      // Small delay to ensure the initial height correction applies instantly without animation
      const timer = setTimeout(() => {
        setSheetAnimationsEnabled(true);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isMobile, mobileViewportHeight, mobileSearchHeaderHeight]);

  const isMapActive = isMobile ? true : viewMode === 'map';
  const isDesktopSplitView = !isMobile && isMapActive;
  const shouldRenderMobileMap = isMobile && enableMap && mobileSheetHeight < mobileSheetFull - 5;
  const isSheetCollapsed = mobileSheetHeight <= MOBILE_SHEET_MAP + 5;
  const isFullyExpanded = isMobile && mobileSheetHeight >= mobileSheetFull - 1;
  
  const effectiveMobileSearchHeight = isMobile && !isMobileSearchOpen ? mobileSearchHeaderHeight : 0;
  
  // Use 100dvh (dynamic viewport height) to match the container and respect mobile browser chrome.
  // On Android/iOS, 100vh includes the address bar, but 100dvh is the actual visible viewport.
  // This ensures the sheet doesn't overlap the search header when the address bar is visible.
  // We provide both a dvh-based value (preferred) and a pixel-based fallback for older browsers.
  const fullSheetHeightValue = isMobile
    ? `calc(100dvh - env(safe-area-inset-bottom, 0px) - ${Math.round(effectiveMobileSearchHeight)}px)`
    : undefined;
  
  // Fallback for browsers that don't support dvh - uses JS-measured viewport height
  const fullSheetHeightFallback = isMobile && mobileViewportHeight
    ? `${mobileViewportHeight - Math.round(effectiveMobileSearchHeight)}px`
    : undefined;
    
  const sheetTransitionClass = sheetAnimationsEnabled && !mobileSheetDragState.current.active
    ? 'transition-[height] duration-300 ease-out'
    : '';
  const sheetOpacityTransition = sheetAnimationsEnabled ? 'opacity 150ms ease-out' : 'none';

  // Update selected item and notify parent
  const updateSelectedItem = useCallback((item) => {
    setSelectedItem(item);
    if (onSelectedItemChange) {
      onSelectedItemChange(item);
    }
  }, [onSelectedItemChange]);

  // Request geolocation
  const requestGeolocationIfNeeded = useCallback(() => {
    if (userLocation || geoRequestedRef.current || !enableMap) return;
    geoRequestedRef.current = true;

    LocationService.getCurrentPosition({ enableHighAccuracy: false, timeout: 3000, maximumAge: 300000 })
      .then((pos) => {
        setUserLocation({ lat: pos.lat, lng: pos.lng });
      })
      .catch((error) => {
        console.warn('Could not get location for map view:', error);
      });

    try {
      LocationService.watchPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords || {};
          if (typeof latitude === 'number' && typeof longitude === 'number') {
            setUserLocation({ lat: latitude, lng: longitude });
          }
          if (geoWatchIdRef.current && geoWatchIdRef.current.clear) {
            try { geoWatchIdRef.current.clear(); } catch {}
            geoWatchIdRef.current = null;
          }
        },
        () => {
          if (geoWatchIdRef.current && geoWatchIdRef.current.clear) {
            try { geoWatchIdRef.current.clear(); } catch {}
            geoWatchIdRef.current = null;
          }
        },
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
      ).then((sub) => { geoWatchIdRef.current = sub; });
    } catch (e) {
      console.warn('Failed to start geolocation watch:', e);
    }
  }, [userLocation, enableMap]);

  // Cleanup geolocation watch
  useEffect(() => {
    return () => {
      if (geoWatchIdRef.current && geoWatchIdRef.current.clear) {
        try { geoWatchIdRef.current.clear(); } catch {}
        geoWatchIdRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    mobileSheetHeightRef.current = mobileSheetHeight;
  }, [mobileSheetHeight]);

  // Measure mobile viewport height (layout viewport)
  useEffect(() => {
    if (!isMobile) {
      setMobileViewportHeight(null);
      return undefined;
    }
    if (typeof window === 'undefined') return undefined;

    const updateViewportHeight = () => {
      // Use innerHeight for layout viewport consistency with vh units
      const nextHeight = window.innerHeight;
      if (nextHeight > 0) {
        hasMeasuredViewportRef.current = true;
        setMobileViewportHeight(nextHeight);
      }
    };

    // Measure immediately on mount to avoid delay
    updateViewportHeight();

    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', updateViewportHeight);

    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
    };
  }, [isMobile]);

  // Measure mobile search header height
  useIsomorphicLayoutEffect(() => {
    if (!isMobile || isMobileSearchOpen) {
      if (!isMobile) {
        setMobileSearchHeaderHeight(SEARCH_BAR_HEIGHT);
      }
      return undefined;
    }
    if (!mobileSearchHeaderRef.current || typeof window === 'undefined') {
      return undefined;
    }

    const element = mobileSearchHeaderRef.current;
    const updateHeaderHeight = () => {
      const rect = element.getBoundingClientRect();
      if (!rect?.height) return;
      const nextHeight = Math.round(rect.height);
      setMobileSearchHeaderHeight((prev) => (Math.abs(prev - nextHeight) > 1 ? nextHeight : prev));
    };

    updateHeaderHeight();

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateHeaderHeight);
      resizeObserver.observe(element);
    }

    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', updateHeaderHeight);
    window.addEventListener('resize', updateHeaderHeight);
    window.addEventListener('orientationchange', updateHeaderHeight);

    return () => {
      resizeObserver?.disconnect();
      viewport?.removeEventListener('resize', updateHeaderHeight);
      window.removeEventListener('resize', updateHeaderHeight);
      window.removeEventListener('orientationchange', updateHeaderHeight);
    };
  }, [isMobile, isMobileSearchOpen]);

  // Hide bottom nav when sheet is collapsed or item preview is open
  useEffect(() => {
    if (isMobile) {
      setBottomNavVisible(!isSheetCollapsed && !selectedItem);
    }
    return () => {
      if (isMobile) {
        setBottomNavVisible(true);
      }
    };
  }, [isMobile, isSheetCollapsed, selectedItem, setBottomNavVisible]);

  useEffect(() => {
    if (!isMobile) {
      setMobileSheetState('list');
      setMobileSheetHeight(DEFAULT_SHEET_FULL);
    }
  }, [isMobile]);

  useEffect(() => {
    if (viewMode === 'map' && enableMap) {
      requestGeolocationIfNeeded();
    }
  }, [viewMode, requestGeolocationIfNeeded, enableMap]);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Ignore viewport changes during initial map setup to prevent auto-enabling filter
  // Reset counter and ignore flag when switching to map view or on mount
  useEffect(() => {
    // Clear any existing timeout
    if (mapInitializationTimeoutRef.current) {
      clearTimeout(mapInitializationTimeoutRef.current);
    }
    
    // Reset counter and start ignoring changes
    viewportChangeCountRef.current = 0;
    ignoreViewportChangesRef.current = true;
    
    // After a delay (2 seconds), start counting viewport changes as user interactions
    // This gives the map time to finish initializing (auto-center, zoom adjustments, etc.)
    mapInitializationTimeoutRef.current = setTimeout(() => {
      ignoreViewportChangesRef.current = false;
      viewportChangeCountRef.current = 0; // Reset counter after initialization period
    }, 2000);
    
    return () => {
      if (mapInitializationTimeoutRef.current) {
        clearTimeout(mapInitializationTimeoutRef.current);
      }
    };
  }, [viewMode, enableMap]); // Reset when switching to map view or when map is enabled

  useEffect(() => {
    if (!isMobile) {
      setMobileSheetState('list');
      return;
    }
    if (enableMap) {
      requestGeolocationIfNeeded();
    }
  }, [isMobile, requestGeolocationIfNeeded, enableMap]);

  // Desktop view change handler
  const handleDesktopViewChange = useCallback((mode) => {
    // Reset viewport change counter when switching views
    if (mode === 'map') {
      viewportChangeCountRef.current = 0;
      ignoreViewportChangesRef.current = true;
      // Clear any existing timeout
      if (mapInitializationTimeoutRef.current) {
        clearTimeout(mapInitializationTimeoutRef.current);
      }
      // Start ignoring changes for 2 seconds after switching to map view
      mapInitializationTimeoutRef.current = setTimeout(() => {
        ignoreViewportChangesRef.current = false;
        viewportChangeCountRef.current = 0;
      }, 2000);
    }
    
    // Update URL for consistency and back button support
    const newParams = new URLSearchParams(searchParams);
    if (mode === 'map') {
      newParams.set('view', 'map');
    } else {
      newParams.delete('view');
    }
    setSearchParams(newParams, { replace: true });
    
    // Always update viewMode, enableMap controls whether map actually renders
    setViewMode(mode);
    viewportChangeCountRef.current = 0;
    
    if (mode === 'map' && enableMap) {
      requestGeolocationIfNeeded();
      setMapFilterManuallyDisabled(false);
    }
    
    if (onViewChange) {
      onViewChange(mode);
    }
  }, [requestGeolocationIfNeeded, enableMap, onViewChange, searchParams, setSearchParams]);

  // Mobile view change handler
  const handleMobileViewChange = useCallback((mode, { updateUrl = true } = {}) => {
    setMobileSheetState(mode);
    if (mode === 'map' && enableMap) {
      setMobileSheetHeight(MOBILE_SHEET_MAP);
      // Reset counter and start ignoring changes when switching to map view
      viewportChangeCountRef.current = 0;
      ignoreViewportChangesRef.current = true;
      // Clear any existing timeout
      if (mapInitializationTimeoutRef.current) {
        clearTimeout(mapInitializationTimeoutRef.current);
      }
      // Start ignoring changes for 2 seconds after switching to map view
      mapInitializationTimeoutRef.current = setTimeout(() => {
        ignoreViewportChangesRef.current = false;
        viewportChangeCountRef.current = 0;
      }, 2000);
      requestGeolocationIfNeeded();
      setMapFilterManuallyDisabled(false);
      if (updateUrl) {
        lastSyncedUrlViewRef.current = 'map';
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('view', 'map');
          return next;
        });
      }
    } else {
      setMobileSheetHeight(mobileSheetFull);
      viewportChangeCountRef.current = 0;
      updateSelectedItem(null);
      if (updateUrl) {
        lastSyncedUrlViewRef.current = null;
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('view');
          return next;
        });
      }
    }
    if (onViewChange) {
      onViewChange(mode);
    }
  }, [requestGeolocationIfNeeded, setSearchParams, enableMap, onViewChange, updateSelectedItem, mobileSheetFull]);

  const handleMobileViewToggle = useCallback(() => {
    handleMobileViewChange(mobileSheetState === 'list' ? 'map' : 'list');
  }, [handleMobileViewChange, mobileSheetState]);

  // Sync viewMode from URL on mount and back button (desktop)
  useEffect(() => {
    if (isMobile) return;
    if (urlViewMode === lastSyncedUrlViewRef.current) return;
    lastSyncedUrlViewRef.current = urlViewMode;
    
    const targetMode = urlViewMode === 'map' ? 'map' : 'list';
    setViewMode(targetMode);
  }, [isMobile, urlViewMode]);

  // Sync sheet state from URL on mount and back button (mobile)
  useEffect(() => {
    if (!isMobile) return;
    if (urlViewMode === lastSyncedUrlViewRef.current) return;
    lastSyncedUrlViewRef.current = urlViewMode;
    
    const targetMode = urlViewMode === 'map' ? 'map' : 'list';
    handleMobileViewChange(targetMode, { updateUrl: false });
  }, [isMobile, urlViewMode, handleMobileViewChange]);

  // Sheet drag handlers
  const handleSheetDragMove = useCallback((event) => {
    if (!mobileSheetDragState.current.active) return;
    event.preventDefault();

    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 1;

    const clientY = event.clientY ?? mobileSheetDragState.current.startY;
    const deltaY = mobileSheetDragState.current.startY - clientY;
    const deltaPercent = (deltaY / (viewportHeight || 1)) * 100;
    const nextHeight = Math.min(
      mobileSheetFull + 5,
      Math.max(MOBILE_SHEET_MAP - 2, mobileSheetDragState.current.startHeight + deltaPercent)
    );

    setMobileSheetHeight(nextHeight);
  }, [mobileSheetFull]);

  const handleSheetDragEnd = useCallback(() => {
    if (!mobileSheetDragState.current.active) return;
    mobileSheetDragState.current.active = false;

    if (typeof document !== 'undefined') {
      document.removeEventListener('pointermove', handleSheetDragMove);
      document.removeEventListener('pointerup', handleSheetDragEnd);
      document.removeEventListener('pointercancel', handleSheetDragEnd);
      document.body.style.userSelect = '';
    }

    const currentHeight = mobileSheetHeightRef.current;
    const snapPoints = [MOBILE_SHEET_MAP, MOBILE_SHEET_PEEK, mobileSheetFull];
    
    let closestSnap = snapPoints[0];
    let closestDistance = Math.abs(currentHeight - snapPoints[0]);
    
    for (const snap of snapPoints) {
      const distance = Math.abs(currentHeight - snap);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestSnap = snap;
      }
    }
    
    setMobileSheetHeight(closestSnap);
    
    let newState = 'list';
    if (closestSnap === MOBILE_SHEET_MAP) {
      newState = 'map';
    } else if (closestSnap === MOBILE_SHEET_PEEK) {
      newState = 'peek';
    }
    setMobileSheetState(newState);
    
    // Update URL to match drag result
    if (closestSnap === MOBILE_SHEET_MAP) {
      lastSyncedUrlViewRef.current = 'map';
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('view', 'map');
        return next;
      });
    } else if (Math.abs(closestSnap - mobileSheetFull) < 1 || closestSnap === MOBILE_SHEET_PEEK) {
      lastSyncedUrlViewRef.current = null;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (next.has('view')) {
          next.delete('view');
          return next;
        }
        return prev;
      });
    }
    
    if (Math.abs(closestSnap - mobileSheetFull) < 1) {
      updateSelectedItem(null);
    }
  }, [handleSheetDragMove, setSearchParams, updateSelectedItem, mobileSheetFull]);

  const handleSheetPointerDown = useCallback((event) => {
    if (!isMobile || mobileSheetDragState.current.active) return;
    event.preventDefault();

    mobileSheetDragState.current = {
      active: true,
      startY: event.clientY ?? 0,
      startHeight: mobileSheetHeightRef.current,
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('pointermove', handleSheetDragMove);
      document.addEventListener('pointerup', handleSheetDragEnd);
      document.addEventListener('pointercancel', handleSheetDragEnd);
      document.body.style.userSelect = 'none';
    }
  }, [isMobile, handleSheetDragMove, handleSheetDragEnd]);

  useEffect(() => {
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('pointermove', handleSheetDragMove);
        document.removeEventListener('pointerup', handleSheetDragEnd);
        document.removeEventListener('pointercancel', handleSheetDragEnd);
      }
    };
  }, [handleSheetDragMove, handleSheetDragEnd]);

  // Map viewport change handler
const handleMapViewportChange = useCallback((center, zoom, radius, bounds) => {
    if (zoom !== undefined) {
      setMapZoomLevel(typeof zoom === 'number' ? zoom : null);
    }
    if (center) {
      setMapViewportCenter(center);
    }
    if (radius !== undefined) {
      setMapViewportRadius(radius);
    }
    
    const nextRadiusValue =
      typeof radius === 'number' && Number.isFinite(radius)
        ? radius
        : (typeof mapViewportRadius === 'number' && Number.isFinite(mapViewportRadius)
            ? mapViewportRadius
            : null);
    let autoDisabledThisTick = false;

    if (!autoEnableMapFilter) {
      const hasRelativeZoomOut =
        activationRadiusRef.current &&
        nextRadiusValue !== null &&
        nextRadiusValue >= activationRadiusRef.current * MAP_FILTER_AUTO_DISABLE_RATIO;

      if (
        mapSyncEnabled &&
        !mapFilterManuallyDisabled &&
        nextRadiusValue !== null &&
        (nextRadiusValue >= MAP_FILTER_AUTO_DISABLE_RADIUS_KM || hasRelativeZoomOut)
      ) {
        setMapSyncEnabled(false);
        setMapUserInteracted(false);
        setMapFilterManuallyDisabled(true);
        viewportChangeCountRef.current = 0;
        autoDisabledDueToRadiusRef.current = true;
        activationRadiusRef.current = null;
        autoDisabledThisTick = true;
      } else if (
        autoDisabledDueToRadiusRef.current &&
        !mapSyncEnabled &&
        nextRadiusValue !== null &&
        (nextRadiusValue <= MAP_FILTER_AUTO_REENABLE_RADIUS_KM ||
          (activationRadiusRef.current &&
            nextRadiusValue <= activationRadiusRef.current * 1.5))
      ) {
        autoDisabledDueToRadiusRef.current = false;
        setMapFilterManuallyDisabled(false);
        setMapUserInteracted(true);
        setMapSyncEnabled(true);
        activationRadiusRef.current = nextRadiusValue ?? activationRadiusRef.current;
        viewportChangeCountRef.current = 4;
      }
    }

    // Format bounds to bbox if provided
    if (bounds) {
      const formatBoundsToBbox = (bounds, fallbackCenter = null, fallbackZoom = null) => {
        const clampLat = (lat) => Math.min(90, Math.max(-90, lat));
        const clampLng = (lng) => Math.min(180, Math.max(-180, lng));
        if (
          Array.isArray(bounds) &&
          bounds.length === 2 &&
          Array.isArray(bounds[0]) &&
          Array.isArray(bounds[1])
        ) {
          const [southLat, westLng] = bounds[0];
          const [northLat, eastLng] = bounds[1];
          if (
            [southLat, westLng, northLat, eastLng].every((value) => Number.isFinite(value))
          ) {
            return `${clampLng(westLng)},${clampLat(southLat)},${clampLng(eastLng)},${clampLat(northLat)}`;
          }
        }
        if (
          fallbackCenter &&
          typeof fallbackCenter.lat === 'number' &&
          typeof fallbackCenter.lng === 'number' &&
          typeof fallbackZoom === 'number' &&
          !Number.isNaN(fallbackZoom)
        ) {
          const latRange = 180 / Math.pow(2, fallbackZoom);
          const lngRange = 360 / Math.pow(2, fallbackZoom);
          const westLng = fallbackCenter.lng - lngRange / 2;
          const eastLng = fallbackCenter.lng + lngRange / 2;
          const southLat = fallbackCenter.lat - latRange / 2;
          const northLat = fallbackCenter.lat + latRange / 2;
          return `${clampLng(westLng)},${clampLat(southLat)},${clampLng(eastLng)},${clampLat(northLat)}`;
        }
        return null;
      };
      
      const bbox = formatBoundsToBbox(bounds, center, zoom);
      if (bbox) {
        setMapViewportBbox((prev) => (prev === bbox ? prev : bbox));
      }
    }
    
    // Only count viewport changes as user interactions after initial map setup is complete
    // This prevents auto-enabling the filter during initial map movements (auto-center, zoom, etc.)
    if (autoEnableMapFilter) {
      if (!mapUserInteracted) {
        setMapUserInteracted(true);
      }
      if (!mapFilterManuallyDisabled && !mapSyncEnabled) {
        setMapSyncEnabled(true);
      }
    } else if (!autoDisabledThisTick && !ignoreViewportChangesRef.current) {
      viewportChangeCountRef.current += 1;
      if (viewportChangeCountRef.current > 3) {
        setMapUserInteracted(true);
        if (mapFilterManuallyDisabled) {
          setMapFilterManuallyDisabled(false);
        }
        if (!mapFilterManuallyDisabled) {
          if (!mapSyncEnabled) {
            activationRadiusRef.current =
              nextRadiusValue ?? mapViewportRadius ?? activationRadiusRef.current;
          }
          setMapSyncEnabled(true);
        }
      }
    }
    
    if (selectedItem) {
      updateSelectedItem(null);
    }
  }, [
    selectedItem,
    mapFilterManuallyDisabled,
    mapSyncEnabled,
    mapViewportRadius,
    updateSelectedItem,
    autoEnableMapFilter,
    mapUserInteracted,
  ]);

  const handleClearMapAreaFilter = useCallback(() => {
    setMapSyncEnabled(false);
    setMapFilterManuallyDisabled(true);
    autoDisabledDueToRadiusRef.current = false;
    activationRadiusRef.current = null;
  }, []);

  const handleEnableMapAreaFilter = useCallback(() => {
    if (!mapViewportBbox) return;
    setMapFilterManuallyDisabled(false);
    activationRadiusRef.current = mapViewportRadius ?? null;
    setMapSyncEnabled(true);
    autoDisabledDueToRadiusRef.current = false;
  }, [mapViewportBbox, mapViewportRadius]);
  
  // Refs for geolocation
  const geoRequestedRef = useRef(false);
  const geoWatchIdRef = useRef(null);
  const viewportChangeCountRef = useRef(0);
  const lastSyncedUrlViewRef = useRef(undefined);
  const hasMeasuredViewportRef = useRef(false);
  const mapInitializationTimeoutRef = useRef(null);
  const ignoreViewportChangesRef = useRef(true); // Start by ignoring changes during initialization
  const autoDisabledDueToRadiusRef = useRef(false);
  const activationRadiusRef = useRef(null);

  return {
    // State
    isMobile,
    viewMode,
    mobileSheetState,
    mobileSheetHeight,
    mobileSheetFull, // Export dynamic full height
    isFullyExpanded, // Export expanded state
    userLocation,
    mapViewportBbox,
    mapViewportCenter,
    mapViewportRadius,
    mapZoomLevel,
    mapUserInteracted,
    mapSyncEnabled,
    mapFilterManuallyDisabled,
    isMobileSearchOpen,
    setIsMobileSearchOpen,
    hasMounted,
    sheetAnimationsEnabled,
    selectedItem,
    setSelectedItem: updateSelectedItem,
    
    // Computed
    isMapActive,
    isDesktopSplitView,
    shouldRenderMobileMap,
    isSheetCollapsed,
    fullSheetHeightValue,
    fullSheetHeightFallback,
    sheetTransitionClass,
    sheetOpacityTransition,
    mobileSearchHeaderRef,
    mobileSearchHeaderHeight,
    
    // Handlers
    handleDesktopViewChange,
    handleMobileViewChange,
    handleMobileViewToggle,
    handleSheetPointerDown,
    handleMapViewportChange,
    handleClearMapAreaFilter,
    handleEnableMapAreaFilter,
    requestGeolocationIfNeeded,
  };
};
